import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { getMe, getValidAccessToken } from '@/services/authService';
import type { UserProfile } from '@/services/authService';
import LevelBadge from './LevelBadge';
import GemIcon from '@/components/icons/GemIcon';

const toPersianDigits = (num: number) =>
  String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const XPBoostBadge: React.FC<{ boostType: 'xp_boost_1_5x' | 'xp_boost_2x' }> = ({ boostType }) => (
  <div className="relative -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#FBBF24] p-0 text-[7px] font-black leading-none text-black shadow-sm" aria-label="تقویت امتیاز فعال">
    <span>{boostType === 'xp_boost_1_5x' ? '۱.۵×' : '۲×'}</span>
  </div>
);

const DashboardHeader: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasVisitedGems, setHasVisitedGems] = useState(() => (
    localStorage.getItem('dashboard_gems_tooltip_seen') === 'true'
  ));

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = await getValidAccessToken();
        if (!token) {
          if (mounted) setIsLoading(false);
          return;
        }
        const userProfile = await getMe();
        if (mounted) {
          if (userProfile) setProfile(userProfile);
          setIsLoading(false);
        }
      } catch (error) {
        console.warn('[DashboardHeader] Failed to load profile data', error);
        if (mounted) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const level = profile?.level ?? 1;
  const currentLevelXp = profile?.current_level_xp ?? 0;
  const nextLevelXp = profile?.next_level_xp ?? 150;
  const gems = profile?.gems ?? 0;
  const activeBoostType = profile?.active_boost_type;
  const progressPercentage = nextLevelXp > 0
    ? Math.min((currentLevelXp / nextLevelXp) * 100, 100)
    : 0;

  if (isLoading) {
    return (
      <div className="px-4 pt-5 pb-4 sm:px-6 sm:pt-6 sm:pb-5" dir="rtl">
        <div
          className="relative -top-3 mx-auto flex h-[66px] max-w-[440px] items-center justify-center rounded-3xl bg-[#FFFDFC] px-3 ring-1 ring-black/10"
          style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
        >
          <div className="flex w-full items-center gap-3 animate-pulse" dir="rtl">
            <div className="h-9 w-9 rounded-full bg-gray-300" />
            <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
              <div className="h-2.5 w-32 rounded-full bg-gray-300" />
              <div className="h-2.5 w-14 rounded bg-gray-300" />
            </div>
            <div className="h-8 w-px bg-gray-300" />
            <div className="h-9 w-24 rounded-full bg-gray-300" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pt-5 pb-4 sm:px-6 sm:pt-6 sm:pb-5" dir="rtl">
      <div className="mx-auto flex max-w-[440px] justify-center">
        {/* RTL order from right to left: Level → XP → divider → Gems. */}
        <div
          className="relative -top-3 flex w-full items-center gap-3 rounded-3xl bg-[#FFFDFC] px-3 py-2 ring-1 ring-black/10"
          style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
        >
          <div className="relative flex shrink-0 items-center justify-center">
            <LevelBadge level={level} className="h-9 w-9 sm:h-10 sm:w-10" />
            {activeBoostType && (
              <div className="absolute -right-1 -top-1 z-20">
                <XPBoostBadge boostType={activeBoostType} />
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-1" dir="rtl">
            <div className="h-3 w-full max-w-[190px] overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercentage}%`, backgroundColor: '#FBBF24' }}
              />
            </div>
            <span className="mt-1 text-right text-[10px] font-bold leading-none text-black sm:text-xs">
              {toPersianDigits(currentLevelXp)} / {toPersianDigits(nextLevelXp)}
            </span>
          </div>

          <div className="h-8 w-px shrink-0 bg-[#D8D2C8]" />

          <div className="relative shrink-0">
            <button
              onClick={() => {
                localStorage.setItem('dashboard_gems_tooltip_seen', 'true');
                setHasVisitedGems(true);
                navigate('/gems');
              }}
              className="inline-flex h-9 items-center gap-1 rounded-3xl bg-cyan-500 px-2 py-0 text-white shadow-md shadow-cyan-500/30 transition-transform duration-200 hover:scale-105 hover:bg-cyan-600"
              title="الماس‌ها"
              aria-label="الماس‌ها"
            >
              <span className="flex size-5 items-center justify-center">
                <GemIcon size={18} color="white" />
              </span>
              <span className="relative top-[1.5px] inline-flex min-w-[2ch] justify-center text-center font-black text-[13px] leading-none sm:text-sm">
                {toPersianDigits(gems)}
              </span>
              <span className="mr-3 flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-white text-cyan-500">
                <Plus className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden="true" />
              </span>
            </button>

            {!hasVisitedGems && (
              <div role="tooltip" className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 w-max max-w-[calc(100vw-1rem)] rounded-md bg-white px-2 py-1 text-right text-[10px] font-bold leading-relaxed text-black shadow-md">
                <span className="absolute -bottom-1 left-3 h-2 w-2 rotate-45 bg-white" />
                فروشگاه و قابلیت‌های ویژه
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;
